import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../api';
import type { RosterMapping, RosterWarning, Runner } from '../types';
import { AddRunnerForm } from './AddRunnerForm';
import { runnersKey, useRunners } from './queries';

interface Preview {
  headers: string[];
  rowCount: number;
  mapping: RosterMapping;
  sample: Record<string, string>[];
  /** Set when the file holds several events (5K and 10K exported together). */
  events: { column: string; values: { value: string; count: number }[] } | null;
}

/** Excel → CSV in the browser, so the server only ever sees CSV. */
async function excelToCsv(file: File): Promise<string> {
  const [{ readSheet }, Papa] = await Promise.all([import('read-excel-file/browser'), import('papaparse')]);
  const rows = await readSheet(file);
  const cell = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : v === null || v === undefined ? '' : String(v));
  return Papa.unparse(rows.map((r) => r.map(cell)));
}

const sourceLabel: Record<Runner['descriptionSource'], string> = {
  none: '—',
  import: 'Roster import',
  email: 'Email',
  checkin_photo: 'Check-in photo',
  staff: 'Staff',
};

function warningText(w: RosterWarning): string {
  if (w.kind === 'duplicate_bib') return `Bib ${w.bib} is assigned to ${w.names.length} runners: ${w.names.join(', ')}.`;
  if (w.kind === 'missing_bib') return `${w.names.length} runner${w.names.length === 1 ? ' has' : 's have'} no bib: ${w.names.slice(0, 5).join(', ')}${w.names.length > 5 ? '…' : ''}`;
  return `Skipped ${w.rows.length} row${w.rows.length === 1 ? '' : 's'} with no name (spreadsheet row ${w.rows.join(', ')}).`;
}

function ColumnSelect({ headers, value, onChange, label, allowNone = true }: { headers: string[]; value: string | null; onChange: (v: string | null) => void; label: string; allowNone?: boolean }) {
  return (
    <label className="field">
      {label}
      <select className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        {allowNone && <option value="">(none)</option>}
        {headers.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </label>
  );
}

function ImportPanel({ raceId }: { raceId: string }) {
  const qc = useQueryClient();
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mapping, setMapping] = useState<RosterMapping | null>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [result, setResult] = useState<{ inserted: number; updated: number; total: number; skippedOtherEvents?: number; warnings: RosterWarning[] } | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: (text: string) => api<Preview>(`/races/${raceId}/roster/preview`, { body: { csv: text } }),
    onSuccess: (p) => {
      setPreview(p);
      setMapping(p.mapping);
    },
  });
  const importMut = useMutation({
    mutationFn: () => api<typeof result & object>(`/races/${raceId}/roster/import`, { body: { csv, mapping, mode } }),
    onSuccess: (r) => {
      setResult(r);
      setPreview(null);
      qc.invalidateQueries({ queryKey: runnersKey(raceId) });
    },
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setReadError(null);
    setResult(null);
    let text: string;
    try {
      if (/\.xls$/i.test(file.name)) throw new Error('Old .xls files can’t be read: save it as .xlsx or CSV.');
      text = /\.xlsx$/i.test(file.name) ? await excelToCsv(file) : await file.text();
    } catch (err) {
      setReadError((err as Error).message);
      return;
    }
    setCsv(text);
    setFileName(file.name);
    previewMut.mutate(text);
  };

  const set = (patch: Partial<RosterMapping>) => setMapping((m) => (m ? { ...m, ...patch } : m));

  return (
    <section className="card stack" style={{ gap: 14 }}>
      <h2>Import roster</h2>
      <label className="field">
        Participant export from your registration platform (CSV or Excel)
        <input
          className="input"
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => onFile(e.target.files?.[0])}
          style={{ paddingTop: 10 }}
        />
        <span className="hint">
          Any export with a header row works: RunSignup, Race Roster, UltraSignup, Active, or your own spreadsheet. Starting from scratch? <a href="/roster-template.csv" download>Download the template</a>.
        </span>
      </label>
      {readError && <div className="notice bad">{readError}</div>}
      {previewMut.error && <div className="notice bad">{(previewMut.error as Error).message}</div>}
      {preview && mapping && (
        <>
          <div className="small muted">
            {fileName}: {preview.rowCount} rows. Check which columns hold what:
          </div>
          <div className="form-grid">
            <ColumnSelect label="Bib" headers={preview.headers} value={mapping.bib} onChange={(v) => set({ bib: v })} />
            <ColumnSelect label="Name (first column)" headers={preview.headers} value={mapping.name[0] ?? null} allowNone={false} onChange={(v) => set({ name: [v!, ...mapping.name.slice(1)] })} />
            <ColumnSelect label="Name (second column)" headers={preview.headers} value={mapping.name[1] ?? null} onChange={(v) => set({ name: v ? [mapping.name[0]!, v] : [mapping.name[0]!] })} />
            <ColumnSelect label="Gender / sex" headers={preview.headers} value={mapping.division[0] ?? null} onChange={(v) => set({ division: [v, mapping.division[1] ?? null].filter((x): x is string => !!x) })} />
            <ColumnSelect label="Age group" headers={preview.headers} value={mapping.division[1] ?? null} onChange={(v) => set({ division: [mapping.division[0] ?? null, v].filter((x): x is string => !!x), ...(v ? { age: null } : {}) })} />
            <ColumnSelect label="Age (makes age groups)" headers={preview.headers} value={mapping.age} onChange={(v) => set({ age: v, ...(v ? { division: mapping.division.slice(0, 1) } : {}) })} />
            {mapping.age && (
              <label className="field">
                Age groups
                <select className="input" value={mapping.ageBand} onChange={(e) => set({ ageBand: Number(e.target.value) as 5 | 10 })}>
                  <option value={10}>10 years (30–39)</option>
                  <option value={5}>5 years (30–34)</option>
                </select>
              </label>
            )}
            <ColumnSelect label="Wave / corral" headers={preview.headers} value={mapping.wave} onChange={(v) => set({ wave: v })} />
            <label className="field">
              Existing runners
              <select className="input" value={mode} onChange={(e) => setMode(e.target.value as 'merge' | 'replace')}>
                <option value="merge">Update by bib, add new</option>
                <option value="replace">Replace the whole roster</option>
              </select>
            </label>
          </div>
          {preview.events && (
            <fieldset className="card stack" style={{ gap: 8, padding: '12px 16px', margin: 0 }}>
              <legend className="small" style={{ fontWeight: 600 }}>
                This file has several events (“{preview.events.column}”). Which are in this race?
              </legend>
              <div className="inline">
                {preview.events.values.map((ev) => {
                  const on = mapping.filter?.values.includes(ev.value) ?? false;
                  return (
                    <label key={ev.value} className="check" style={{ alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          const values = on ? (mapping.filter?.values ?? []).filter((v) => v !== ev.value) : [...(mapping.filter?.values ?? []), ev.value];
                          set({ filter: values.length ? { column: preview.events!.column, values } : null });
                        }}
                      />
                      {ev.value || '(blank)'} <span className="muted small">({ev.count})</span>
                    </label>
                  );
                })}
              </div>
              <span className="hint">Each race in Splitline is one course. Create a separate race for each other event and import the same file there.</span>
            </fieldset>
          )}
          {importMut.error && <div className="notice bad">{(importMut.error as Error).message}</div>}
          <div className="inline">
            <button
              type="button"
              className="btn primary"
              disabled={importMut.isPending || mapping.name.length === 0 || (!!preview.events && !mapping.filter)}
              onClick={() => importMut.mutate()}
            >
              Import {mapping.filter && preview.events ? preview.events.values.filter((v) => mapping.filter!.values.includes(v.value)).reduce((n, v) => n + v.count, 0) : preview.rowCount} runners
            </button>
            <button type="button" className="btn" onClick={() => setPreview(null)}>
              Cancel
            </button>
          </div>
        </>
      )}
      {result && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="notice ok">
            Added {result.inserted}, updated {result.updated}
            {result.skippedOtherEvents ? `, skipped ${result.skippedOtherEvents} in other events` : ''}. The roster now has {result.total} runners.
          </div>
          {result.warnings.map((w, i) => (
            <div className="notice warn" key={i}>
              {warningText(w)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DescriptionCell({ runner, raceId }: { runner: Runner; raceId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(runner.descriptionText ?? '');
  const save = useMutation({
    mutationFn: () => api(`/runners/${runner.id}`, { method: 'PATCH', body: { descriptionText: text || null } }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: runnersKey(raceId) });
    },
  });
  if (!editing) {
    return (
      <button type="button" className="btn link" style={{ textAlign: 'left', color: runner.descriptionText ? 'var(--ink)' : 'var(--warn-fg)', fontStyle: runner.descriptionText ? 'normal' : 'italic', fontWeight: 400 }} onClick={() => setEditing(true)}>
        {runner.descriptionText || 'Add description'}
      </button>
    );
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <textarea className="input" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Green singlet, white cap, black shorts" autoFocus aria-label={`Description for ${runner.name}`} />
      <div className="inline">
        <button type="button" className="btn small primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </button>
        <button type="button" className="btn small" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Race-day registrations, one at a time. */
function AddRunner({ raceId }: { raceId: string }) {
  return (
    <details className="card" style={{ padding: '14px 18px' }}>
      <summary style={{ fontWeight: 600, cursor: 'pointer' }}>Add a runner (late or race-day registration)</summary>
      <div style={{ marginTop: 12 }}>
        <AddRunnerForm raceId={raceId} />
      </div>
    </details>
  );
}

export function RosterPage() {
  const { raceId = '' } = useParams();
  const [q, setQ] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const runners = useRunners(raceId, q, missingOnly);
  const list = runners.data?.runners ?? [];

  return (
    <main className="page">
      <section className="page-head">
        <div>
          <h1>Roster</h1>
          <p>Every registered runner and their bib. When a bib is folded, covered or blurred, Splitline recognises the runner by how they looked at the points where their bib was read.</p>
        </div>
      </section>

      <ImportPanel raceId={raceId} />
      <AddRunner raceId={raceId} />

      <section className="card flush" id="runners">
        <div className="card-head" style={{ alignItems: 'center' }}>
          <h2 style={{ marginRight: 'auto' }}>Runners {runners.data ? `(${list.length})` : ''}</h2>
          <label className="check" style={{ alignItems: 'center' }}>
            <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} />
            Missing description only
          </label>
          <input className="input" type="search" placeholder="Search bib, name or outfit" aria-label="Search runners" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 280, maxWidth: '100%' }} />
        </div>
        {list.length === 0 ? (
          <p className="empty">{runners.isLoading ? 'Loading…' : 'No runners match.'}</p>
        ) : (
          <div className="table-wrap">
            <table className="data" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th>Bib</th>
                  <th>Runner</th>
                  <th>Division</th>
                  <th>Wave</th>
                  <th style={{ width: '38%' }}>Description</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{r.bib ? <span className="bib">{r.bib}</span> : <span className="chip warn">no bib</span>}</td>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>{r.division ?? '—'}</td>
                    <td>{r.wave ?? '—'}</td>
                    <td>
                      <DescriptionCell runner={r} raceId={raceId} />
                      {r.seenAs && (
                        <div className="small" style={{ marginTop: 4 }} title={`From ${r.sightings} crossing${r.sightings === 1 ? '' : 's'} where their bib was read or confirmed`}>
                          <span className="chip info">Seen</span> {r.seenAs}
                        </div>
                      )}
                    </td>
                    <td className="muted">{sourceLabel[r.descriptionSource]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
